import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { MarkInput, Pagination } from '@components'
import Breadcrumb from '@components/Breadcrumb'
import Button from '@components/Button'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Flex from '@components/Flex'
import PageTitle from '@components/PageTitle'
import SearchInput from '@components/SearchInput'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Template from '@components/Template'
import Tr from '@components/Tr'
import { successMessages } from '@constants/feedbackMessages'
import { PencilAltIcon, TrashIcon } from '@heroicons/react/outline'
import { commentsModuleApi } from '@services/commentsModuleApi'
import { loadingModal, openAlertModal, openModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'
import Formatters from '@utils/helpers/Formatters'
import { useFilters, useInputBounce, useQuery } from '@utils/hooks'

import AnswerModule from './EditModule'

const styles = {
	searchContainer: 'gap-[8px] mb-[32px] items-end flex-wrap',
	tag: 'font-medium text-blue-600 underline underline-offset-2',
	divider: 'mt-[32px] mb-[12px]',
	avatar: 'rounded-full max-w-[40px] max-h-[40px] min-w-[40px] min-h-[40px]',
}

function CommentsModule() {
	const dispatch = useAppDispatch()
	const pages = [{ name: 'Comentários de Modulo', href: '#', current: true }]
	const query = useQuery()
	const navigate = useNavigate()

	const pageQuery = Number(query.get('page')) || 1
	const searchQs = query.get('search') || ''

	const [search, setSearch] = useState(decodeURIComponent(searchQs))
	const [searchQuery, setSearchQuery] = useState(decodeURIComponent(searchQs))
	const [page, setPage] = useState(pageQuery)

	const { data, isLoading } = commentsModuleApi.useListQuery({
		page,
		search: encodeURIComponent(searchQuery),
	})

	const [
		deleteCommentsModule,
		{ isLoading: loadingDelete, isSuccess: deleteSuccess },
	] = commentsModuleApi.useDeleteMutation()

	const searchCallback = useCallback(
		(value: string) => {
			navigate('/comentarios-modulo?page=1')
			setSearchQuery(value)
		},
		[navigate]
	)

	useEffect(() => {
		dispatch(loadingModal({ loading: loadingDelete }))

		if (deleteSuccess) {
			dispatch(notificationCreators.show(successMessages.deleteErrorModule))
		}
	}, [dispatch, loadingDelete, deleteSuccess])

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	useInputBounce(search, searchCallback)

	useFilters({
		search: searchQuery,
		fallbackUrl: '/comentarios-modulo',
	})

	const [checkModule, { isSuccess }] =
		commentsModuleApi.useCheckModuleMutation()

	// eslint-disable-next-line no-shadow
	function onSubmit(data: { id: number; isApproved: boolean }) {
		checkModule({
			id: data.id,
			isApproved: data.isApproved,
		})
	}

	useEffect(() => {
		if (isSuccess) {
			dispatch(
				notificationCreators.show(successMessages.updatedStatusComentModule)
			)
		}
	}, [isSuccess, dispatch, navigate])

	return (
		<>
			<Template>
				<Breadcrumb pages={pages} />
				<PageTitle title='Comentários de Modulo' />

				<Flex className={styles.searchContainer}>
					<SearchInput
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className='flex-1'
						placeholder='Buscar Comentário de Modulo'
					/>
				</Flex>

				{isLoading ? (
					<div className='flex justify-center'>
						<Spinner size='md' />
					</div>
				) : (
					<Table
						th={[
							{ label: 'id' },
							{ label: 'aluno' },
							{ label: 'Modulo' },
							{ label: 'Conteúdo' },
							{ label: 'Comentário' },
							{ label: 'Data Cadastro' },
							{ label: 'Aprovar' },
							{ label: 'ações' },
						]}
					>
						{data?.data && data?.data?.length > 0 ? (
							data?.data.map((commentModule) => (
								<Tr key={commentModule.id}>
									<Td>{commentModule.id}</Td>
									<Td className='w-[75%] px-6'>{commentModule.client.name}</Td>
									<Td>{commentModule.content.module.name}</Td>
									<Td>{commentModule.content.name}</Td>
									<Td>
										<div
											dangerouslySetInnerHTML={{ __html: commentModule.text }}
										/>
									</Td>
									<Td>
										{Formatters.createdAt(commentModule.createdAt as string)}
									</Td>
									<Td>
										<MarkInput
											type='checkbox'
											itens={[{ id: '1', title: '' }]}
											defaultChecked={Boolean(commentModule.isApproved)}
											multiCheckbox
											onChange={(e) =>
												onSubmit({
													id: commentModule.id,
													isApproved: e.currentTarget.checked,
												})
											}
										/>
									</Td>

									<Td>
										<Flex className='items-center gap-4'>
											<Button
												className='!px-2 !py-2'
												leftIcon={<PencilAltIcon />}
												disabled={false}
												variant='outline'
												onClick={() =>
													dispatch(
														openModal({
															name: 'answerModule',
															modalInfo: commentModule.id,
														})
													)
												}
											>
												<></>
											</Button>
											<Button
												className='!px-2 !py-2'
												leftIcon={<TrashIcon />}
												disabled={false}
												variant='outline'
												onClick={() => {
													const removeCommentsModule = {
														title: 'Remover comentario de Modulo',
														message:
															'Tem certeza que deseja remover o comentario do Modulo?',
														buttonLabel: 'Remover Comentario do Modulo',
														onClick: () =>
															deleteCommentsModule({
																id: commentModule.id,
															}),
													}

													dispatch(openAlertModal(removeCommentsModule))
												}}
											>
												<></>
											</Button>
										</Flex>
									</Td>
								</Tr>
							))
						) : (
							<EmptyRow tdAmount={8} message='Nenhum comentário encontrado' />
						)}
					</Table>
				)}

				<Divider className={styles.divider} />

				{data?.data && data?.data?.length > 0 && (
					<Pagination
						pageCount={data?.meta.lastPage || 0}
						maxAmount={data?.meta.total || 0}
						perPage={data?.meta.perPage || 0}
					/>
				)}
			</Template>
			<AnswerModule />
		</>
	)
}

export default CommentsModule
